import { useState, useCallback, useEffect } from 'react';
import { auditService } from '../services/audit/auditService';
import {
  AuditLog,
  AuditLogFilter,
  SecuritySettings,
  UpdateSecuritySettingsDto,
  ActiveSession,
  LoginHistoryEntry,
} from '../types/audit';

const DEFAULT_FILTER: AuditLogFilter = {
  action: '',
  entity_type: '',
  user_id: '',
  date_from: '',
  date_to: '',
  search: '',
  page: 1,
  limit: 25,
};

export const useAuditLog = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState<AuditLogFilter>({ ...DEFAULT_FILTER });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const loadLogs = useCallback(async (activeFilter: AuditLogFilter) => {
    setIsLoading(true);
    try {
      const res = await auditService.getAuditLogs(activeFilter);
      if (res.success) {
        setLogs(res.data.logs);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      }
    } catch (e) {
      console.error('Failed to load audit logs', e);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => { loadLogs({ ...DEFAULT_FILTER }); }, [loadLogs]);

  const updateFilter = useCallback((patch: Partial<AuditLogFilter>) => {
    const next: AuditLogFilter = { ...filter, ...patch };
    if (!('page' in patch)) next.page = 1;
    setFilter(next);
    loadLogs(next);
  }, [filter, loadLogs]);

  const resetFilter = useCallback(() => {
    const next = { ...DEFAULT_FILTER };
    setFilter(next);
    loadLogs(next);
  }, [loadLogs]);

  const exportLogs = useCallback(async (): Promise<AuditLog[]> => {
    const res = await auditService.exportAuditLogs(filter);
    return res.success ? res.data : [];
  }, [filter]);

  const getEntityHistory = useCallback(async (entityType: string, entityId: string) => {
    const res = await auditService.getEntityHistory(entityType, entityId);
    return res.success ? res.data : [];
  }, []);

  return {
    logs,
    total,
    totalPages,
    filter,
    isLoading,
    selectedLog,
    setSelectedLog,
    updateFilter,
    resetFilter,
    refresh: () => loadLogs(filter),
    exportLogs,
    getEntityHistory,
  };
};

export const useSecuritySettings = () => {
  const [settings, setSettings] = useState<SecuritySettings | null>(null);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loginHistory, setLoginHistory] = useState<LoginHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [settingsRes, sessionsRes, historyRes] = await Promise.all([
        auditService.getSecuritySettings(),
        auditService.getActiveSessions(),
        auditService.getLoginHistory(),
      ]);
      if (settingsRes.success) setSettings(settingsRes.data);
      if (sessionsRes.success) setSessions(sessionsRes.data);
      if (historyRes.success) setLoginHistory(historyRes.data);
    } catch (e) {
      console.error('Failed to load security data', e);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = useCallback(async (dto: UpdateSecuritySettingsDto) => {
    setIsSaving(true);
    const res = await auditService.updateSecuritySettings(dto);
    if (res.success) setSettings(res.data);
    setIsSaving(false);
    return res;
  }, []);

  const revokeSession = useCallback(async (id: string) => {
    const res = await auditService.revokeSession(id);
    if (res.success) setSessions(prev => prev.filter(s => s.session_id !== id));
    return res;
  }, []);

  return {
    settings,
    sessions,
    loginHistory,
    isLoading,
    isSaving,
    saveSettings,
    revokeSession,
    refresh: loadAll,
  };
};
